import React, { useState, useEffect } from "react";
import { authFetch, apiUrl } from "../../utils/api";

/**
 * MakoTextCard — "Text Mako": the SMS door next to the Telegram door.
 * Same brain, same files, same skills. Linking proves both the account and
 * the phone: Settings mints a one-time code, the member texts it to Mako.
 * Backend: GET /api/mako/sms/status, POST /api/mako/sms/link-code,
 * POST /api/mako/sms/unlink (flag mako_sms; card hides on 404).
 */
export default function MakoTextCard() {
  const [status, setStatus] = useState(null);
  const [code, setCode] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const r = await authFetch(apiUrl("/api/mako/sms/status"));
      if (r.ok) setStatus(await r.json());
    } catch (_) { /* hidden */ }
  };
  useEffect(() => { load(); }, []);

  // While a code is showing, poll so the card flips to "linked" on its own.
  useEffect(() => {
    if (!code || status?.linked) return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [code, status?.linked]);

  if (!status) return null;

  const getCode = async () => {
    setBusy(true); setError("");
    try {
      const r = await authFetch(apiUrl("/api/mako/sms/link-code"), { method: "POST" });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || "Couldn't make a code");
      setCode(d);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  const unlink = async () => {
    setBusy(true);
    try { await authFetch(apiUrl("/api/mako/sms/unlink"), { method: "POST" }); setCode(null); await load(); }
    finally { setBusy(false); }
  };

  const box = { marginBottom: "40px", padding: "24px", borderRadius: "12px", border: "1px solid var(--border-primary)", background: "var(--bg-tertiary)" };
  const btn = { background: "var(--accent-primary)", color: "var(--text-on-accent)", border: "none", borderRadius: "8px", padding: "10px 18px", fontWeight: 600, fontSize: "14px", cursor: "pointer" };

  return (
    <div className={`hf-panel${status.linked ? " hf-connected" : ""}`} style={box}>
      <h3 style={{ margin: 0, fontSize: "18px" }}>🦈 Text Mako</h3>
      <p style={{ margin: "4px 0 0", color: "var(--text-secondary)", fontSize: "14px" }}>
        Text your agent from your regular phone, no app. Same brain, files and skills as here and on Telegram.
      </p>

      {status.linked ? (
        <div style={{ marginTop: "14px", fontSize: "14px" }}>
          <div>
            Linked to your number ending <strong>{status.phoneTail}</strong>
            {status.status === "paused" ? " · paused (you texted STOP; text START to resume)" : ""}. Mako's number:{" "}
            <strong>{status.number}</strong>
          </div>
          <button onClick={unlink} disabled={busy} style={{ ...btn, marginTop: "12px", background: "transparent", color: "var(--text-primary)", border: "1px solid var(--border-primary)" }}>
            Unlink this number
          </button>
        </div>
      ) : code ? (
        <div style={{ marginTop: "14px", fontSize: "14px" }}>
          From your phone, text <strong style={{ fontSize: "16px", letterSpacing: "1px" }}>{code.code}</strong> to{" "}
          <strong>{code.number}</strong>. This card updates when it lands. The code works for 30 minutes.
        </div>
      ) : (
        <button onClick={getCode} disabled={busy} style={{ ...btn, marginTop: "14px" }}>
          {busy ? "One sec…" : "Text Mako from my phone"}
        </button>
      )}
      {!status.sendingConfigured && (
        <p style={{ marginTop: "10px", fontSize: "12px", color: "var(--text-tertiary)" }}>Texting is being switched on; replies may not send yet.</p>
      )}
      {error && <p style={{ marginTop: "10px", color: "var(--error, #ef4444)", fontSize: "13px" }}>{error}</p>}
    </div>
  );
}
